// Tracks when the user last did something in *this* tab, so requests the app makes on
// its own can be declared as background. Without it, a tab left open on an unattended
// machine keeps its session alive forever: the token refresh and polling look like a
// working user.
//
// Deliberately per-tab. The server slides the idle window for any tab's real request, so
// an active tab already keeps the session alive for the idle ones — and an idle tab's
// polling should declare itself background regardless of what another tab is doing.

/**
 * Slack on top of the server's idle window, because a request that is not declared as
 * background slides that window a full period from when it arrives. About a minute: long
 * enough that the requests one gesture fans out into all count as that gesture, short
 * enough that a 30-minute policy cannot become a 35-minute one.
 */
export const BACKGROUND_AFTER_IDLE_MS = 60 * 1000;

/** Events that mean a person is there. Passive and capturing, so nothing is swallowed. */
const INTERACTION_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'submit'] as const;

const LISTENER_OPTIONS: AddEventListenerOptions = { passive: true, capture: true };

// Not seeded to "now": mounting the app is not a person doing something, and a restored
// tab on an unattended machine would otherwise re-arm the idle clock.
let lastInteractionAt = 0;
let listening = false;

const recordInteraction = (): void => {
	lastInteractionAt = Date.now();
};

export const getLastInteractionAt = (): number => lastInteractionAt;

export const getMsSinceLastInteraction = (): number => Date.now() - lastInteractionAt;

export const isUserPresumedIdle = (): boolean =>
	getMsSinceLastInteraction() >= BACKGROUND_AFTER_IDLE_MS;

/** Idempotent; returns a teardown. Repeated calls do not stack listeners. */
export const startTrackingSessionActivity = (): (() => void) => {
	if (listening) {
		return () => undefined;
	}
	listening = true;

	INTERACTION_EVENTS.forEach((event) =>
		window.addEventListener(event, recordInteraction, LISTENER_OPTIONS)
	);

	return () => {
		INTERACTION_EVENTS.forEach((event) =>
			window.removeEventListener(event, recordInteraction, LISTENER_OPTIONS)
		);
		listening = false;
	};
};

/** Test seam: pretend the user last did something at this moment. */
export const setLastInteractionAtForTest = (timestamp: number): void => {
	lastInteractionAt = timestamp;
};
